import { Graph } from '@antv/g6';
import { NODE_TYPES } from '../shapes';
import { Node } from '../types';
import { traverseNodes } from './graph';
import { isHeadNode } from './helpers';

interface NodeDetail {
  node: Node;
  layerIndex: number;
  branchIndex: number;
}

type NodeDetailMap = { [key: string | number]: NodeDetail };
type IdMap = { [key: string | number]: string[] };

interface NodeTreeDetail {
  /** Vertical slices in tree */
  branches: IdMap;
  /** Horizontal slices in tree */
  layers: IdMap;
  /** First node in a tree */
  head: Node;
  /** Tree details for each node */
  nodeDetails: NodeDetailMap;
  /** Ids of all nodes in this tree */
  nodes: string[];
}

/** Gets the details of the tree for a specific node */
export const getNodeTree = (incomingNode: Node): NodeTreeDetail => {
  const nodeDetails: NodeDetailMap = {};
  const layers: IdMap = {};
  const branches: IdMap = {};

  traverseNodes(incomingNode, {
    nodeFn: ({ node, layerIndex, branchIndex }) => {
      const id = node.getID();
      layers[layerIndex] = [...(layers[layerIndex] || []), id];
      branches[branchIndex] = [...(branches[branchIndex] || []), id];
      nodeDetails[id] = { ...(nodeDetails[id] || {}), node, layerIndex, branchIndex };
    },
  });

  return {
    branches,
    head: incomingNode,
    layers,
    nodeDetails,
    nodes: Object.keys(nodeDetails),
  };
};

/**
 * Sorts the trees in the graph by whether they contain start nodes and the
 * number of nodes
 */
export const sortNodeTrees = (trees: NodeTreeDetail[]): NodeTreeDetail[] =>
  trees.sort((a, b) => {
    const aHeadIsStart = a.head.getModel().type === NODE_TYPES.START;
    const bHeadIsStart = b.head.getModel().type === NODE_TYPES.START;
    // Branch with start node as head is ordered first
    if (aHeadIsStart && !bHeadIsStart) return -1;
    if (!aHeadIsStart && bHeadIsStart) return 1;
    // Then order goes in descending number of nodes
    return b.nodes.length - a.nodes.length;
  });

/** Returns an array of tree details in the graph. */
export const getNodeTrees = (graph: Graph): NodeTreeDetail[] =>
  sortNodeTrees(graph.getNodes().filter(isHeadNode).map(getNodeTree));

/** Sorts nodes in a layer so that the order of the branches is correct */
const sortLayer = (layer: string[], nodeDetails: NodeDetailMap) =>
  layer.sort((a, b) => nodeDetails[a].branchIndex - nodeDetails[b].branchIndex);

/**
 * Gets the layers of the node trees in the graph, ordered by their branch index
 */
export const getGraphLayers = (graph: Graph): NodeTreeDetail['layers'] => {
  type TreeAcc = Pick<NodeTreeDetail, 'layers' | 'nodeDetails'> & { numBranches: number };

  const adjustBranchIndex = (tree: NodeTreeDetail, tAcc: TreeAcc) => {
    // Update tree with the new branch index
    const updatedTree = Object.entries(tree.nodeDetails).reduce((acc, [id, detail]) => {
      const { branchIndex: i, layerIndex } = detail;
      const { nodeDetails, layers } = acc;

      return acc.nodeDetails[id]
        ? acc // Return the original set of details if the node details already exist
        : {
            ...acc,
            nodeDetails: { ...nodeDetails, [id]: { ...detail, branchIndex: i + tAcc.numBranches } },
            layers: { ...layers, [layerIndex]: [...(layers[layerIndex] || []), id] },
          };
    }, tAcc);

    // Update the number of branches in the tree
    return { ...updatedTree, numBranches: tAcc.numBranches + Object.values(tree.branches).length };
  };

  /**
   * BEFORE:
   * [
   *  {layers: {0: ['a', 'b'], 1: ['c']}, nodeDetails: {a: {branchIndex:0}, b: {branchIndex:1}, c: {branchIndex:0}}},
   *  {layers: {0: ['d'], 1: ['f', 'e']}, nodeDetails: {d: {branchIndex:0}, e: {branchIndex:0}, f: {branchIndex:1}}},
   * ]
   *
   * AFTER:
   * {
   *  layers: { // combinded into one object
   *    0: ['d', 'a', 'b'],
   *    1: ['c', 'f', 'e']
   *  },
   *  nodeDetails: { // combinded into one object
   *    a: {branchIndex:0},
   *    b: {branchIndex:1},
   *    c: {branchIndex:0},
   *    d: {branchIndex:2},
   *    e: {branchIndex:1},
   *    f: {branchIndex:2}
   *  }
   * }
   */

  const { layers, nodeDetails } = getNodeTrees(graph).reduce(
    (acc: TreeAcc, tree) => adjustBranchIndex(tree, acc),
    { layers: {}, nodeDetails: {}, numBranches: 0 },
  );

  /**
   * Layer Sorting
   *
   * BEFORE:
   * {
   *  layers: {
   *    0: ['d', 'a', 'b'],
   *    1: ['c', 'f', 'e']
   *  },
   *  nodeDetails: { a: {branchIndex:0}, b: {branchIndex:1}, c: {branchIndex:0}, d: {branchIndex:2}, e: {branchIndex:1}, f: {branchIndex:2}}
   * }
   *
   * AFTER:
   * {
   *  layers: {
   *    0: ['a', 'b', 'd'], // now ordered by `branchIndex` in `nodeDetails`
   *    1: ['c', 'e', 'f']  // now ordered by `branchIndex` in `nodeDetails`
   *  },
   *  nodeDetails: { a: {branchIndex:0}, b: {branchIndex:1}, c: {branchIndex:0}, d: {branchIndex:2}, e: {branchIndex:1}, f: {branchIndex:2}}
   * }
   */

  return Object.entries(layers).reduce(
    (acc, [i, layer]) => ({ ...acc, [i]: sortLayer(layer, nodeDetails) }),
    {},
  );
};
